<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useHeaderCollapse } from './useHeaderCollapse';

import ApplicationStatusPicker from '@/components/Conversations/Application/ApplicationStatusPicker.vue';
import ApplicationScoreCompactDisplay from '@/components/Conversations/Application/ApplicationScoreCompactDisplay.vue';
import ProHeaderContactDetails from './ProHeaderContactDetails.vue';
import ProProfileService from '@/core/conversations/pro-profile/pro-profile.service';

import ApplicationScoreHoverWrapper from '../Application/ApplicationScoreHoverWrapper.vue';

import {
  type ATSApplicationScoringProScoring,
  JobApplicantStatus,
} from '@factoryfixinc/ats-interfaces';
import { TrackingActionName } from '@/core/shared/tracking/tracking-actions';
import TrackingService from '@/core/shared/tracking/tracking.service';
import ConversationIndexService from '@/core/conversations/conversation-index/conversation-index.service';
import { navigateAfterConversationStatusChange } from '@/composables/jobs/useConversationStatusNavigation';
import ProjectService from '@/core/shared/project/project.service';
import ProNotesService from '@/core/shared/pro-notes/pro-notes.service';
import { getJobTitle } from '@/core/sourcing/utils/job';
import DoNotContactChip from '@/components/Common/DoNotContactChip.vue';
import ApplicantOriginChip from '@/components/Common/ApplicantOriginChip.vue';
import { isApplicationNotSourcedByAiRecruiter } from '@/core/conversations/application-score/application-score.util';
import DownloadProfile from '../Application/DownloadProfile.vue';
import InterviewSchedulingService from '@/core/shared/interview-scheduling/interview-scheduling.service';
import { ErrorService } from '@/core/shared/errors/error.service';
import InterviewSchedulingTrigger from '@/components/Interview/InterviewSchedulingTrigger.vue';
import InterviewSchedulingGatedButton from '@/components/Interview/InterviewSchedulingGatedButton.vue';
import { EmployerFeatureGatingService } from '@/core/shared/employer-feature-gating/employer-feature-gating.service';
import { FeatureName } from '@factoryfixinc/ats-interfaces';
import ProHeaderActionsMenu from './ProHeaderActionsMenu.vue';
import DoNotContactModal, {
  type DoNotContactModalVariant,
} from '@/components/Conversations/DoNotContact/DoNotContactModal.vue';
import DoNotContactService from '@/core/conversations/do-not-contact/do-not-contact.service';

const emit = defineEmits<{
  (e: 'change:pro-score', value: ATSApplicationScoringProScoring): void;
}>();

const proProfileService = new ProProfileService();
const projectService = new ProjectService();
const conversationIndexService = new ConversationIndexService();
const proNotesService = new ProNotesService();
const interviewSchedulingService = new InterviewSchedulingService();
const featureGatingService = new EmployerFeatureGatingService();
const doNotContactService = new DoNotContactService();
const route = useRoute();
const router = useRouter();

const projectName = ref<string>('');

const headerRow = ref<HTMLElement | null>(null);
const candidateName = ref<HTMLElement | null>(null);
const scoreBadge = ref<HTMLElement | null>(null);
const originChip = ref<HTMLElement | null>(null);
const headerActions = ref<HTMLElement | null>(null);

const {
  hideJobTitle,
  isOriginChipIconOnly,
  shortInterviewLabel,
  isWrapped,
  isNameTruncated,
  remeasure: remeasureHeader,
  reset: resetHeader,
} = useHeaderCollapse({
  row: headerRow,
  name: candidateName,
  scoreBadge,
  originChip,
  actions: headerActions,
});

const userProfile = computed(() => proProfileService.selectedProProfile);
const jobApplicant = computed(() => proProfileService.selectedProApplication);
const computedJobApplicantId = computed(() => jobApplicant.value?.id);
const fullName = computed(() =>
  [userProfile.value?.nameFirst, userProfile.value?.nameLast].filter(Boolean).join(' '),
);
const showCopilotOutreach = computed(
  () => jobApplicant.value?.initiatedByOutreach && jobApplicant.value?.initiatedByCopilot,
);

const showManualSourced = computed(() =>
  isApplicationNotSourcedByAiRecruiter(
    jobApplicant.value?.initiatedByOutreach,
    jobApplicant.value?.initiatedByCopilot,
  ),
);

const atsIntegrationId = computed(
  () => conversationIndexService.selectedConversationIndex?.primaryApplicationIndex?.integrationId,
);

const isDoNotContact = computed(() => doNotContactService.isSelectedProDoNotContact);
const isDoNotContactModalOpen = ref(false);
const doNotContactModalVariant = ref<DoNotContactModalVariant>('mark');
const isDoNotContactSubmitting = ref(false);

function openDoNotContactModal(variant: DoNotContactModalVariant) {
  doNotContactModalVariant.value = variant;
  isDoNotContactModalOpen.value = true;
}

// Stays open on failure: the service already showed the error, so the recruiter can retry.
async function submitDoNotContact(reason: string) {
  isDoNotContactSubmitting.value = true;
  try {
    const succeeded =
      doNotContactModalVariant.value === 'mark'
        ? await doNotContactService.markSelectedPro(reason)
        : await doNotContactService.unmarkSelectedPro();
    if (succeeded) isDoNotContactModalOpen.value = false;
  } finally {
    isDoNotContactSubmitting.value = false;
  }
}

const hasInterviewSchedulingAccess = computed(() =>
  featureGatingService.hasAccess(FeatureName.INTERVIEW_SCHEDULING),
);

async function loadProjectName() {
  projectName.value = getJobTitle(jobApplicant.value?.job, false).displayTitle;
}

const loadData = async () => {
  await Promise.all([loadProjectName(), checkForOngoingInterviewThread()]);
};

async function updateApplicationStatus(
  newStatus: JobApplicantStatus,
  previousStatus: JobApplicantStatus,
  reason: string,
  isSilent: boolean,
) {
  if (!newStatus) {
    return;
  }
  const currentStatus = proProfileService.selectedProApplication?.status;
  const proId = proProfileService.selectedProProfile?.pro.id;
  // Capture the moved candidate before any mutation so the snackbar can link back to them.
  const movedConversationId =
    conversationIndexService.selectedConversationIndex?.conversationId ?? null;
  if (computedJobApplicantId.value) {
    await proProfileService.changeApplicationStatus(
      computedJobApplicantId.value,
      newStatus,
      reason,
      isSilent,
    );

    if (newStatus === JobApplicantStatus.REJECTED && reason && proId) {
      await proNotesService.createProRejectedNote(proId, reason);
    }

    const advanceResult = conversationIndexService.filterLocalConversationIndexesByStatus(
      computedJobApplicantId.value,
      newStatus,
    );

    // To avoid double counting for REJECTED:
    if (previousStatus.includes('Reject')) {
      previousStatus = JobApplicantStatus.REJECTED;
    }

    projectService.updateLocalProjectJobApplicationStatusCount({
      jobId: Number(proProfileService.selectedProApplication?.jobId),
      newStatus,
      previousStatus,
    });

    await proProfileService.getApplicantStatusHistory();

    const projectId = projectService.currentProject?.id;
    if (projectId) {
      await navigateAfterConversationStatusChange({
        router,
        route,
        projectId,
        advanceResult,
        movedConversationId,
        newStatus,
      });
    }
  }

  TrackingService.trackAction(TrackingActionName.APPLICATION_STATUS_CHANGED, {
    source: 'Conversation Top Banner',
    from_status: currentStatus,
    to_status: newStatus,
  });
}

async function checkForOngoingInterviewThread() {
  try {
    await interviewSchedulingService.updateOngoingInterviewThreadCheck();
  } catch (error) {
    ErrorService.captureException(error);
  }
}

watch(computedJobApplicantId, async () => {
  resetHeader();
  await loadData();
});

onMounted(async () => {
  await loadData();
  await nextTick();
  remeasureHeader();
});
defineExpose({ openRemoveDoNotContact: () => openDoNotContactModal('remove') });
</script>

<template>
  <section class="p-4 pb-2">
    <div class="flex justify-between gap-3">
      <!-- `min-w-0` so this column tracks the panel's width instead of being floored by the
           min-content width of the contact details below, which would stop the header from
           collapsing at all once the panel got narrow. -->
      <div class="min-w-0 flex-auto">
        <div
          ref="headerRow"
          class="relative flex min-h-8 w-full items-center"
          :class="{ 'mb-1 flex-wrap': isWrapped }"
        >
          <div
            ref="candidateName"
            class="sensitive min-w-0 truncate font-serif text-lg font-black leading-6 text-black"
            :class="{ 'flex-1': isWrapped }"
          >
            {{ userProfile?.nameFirst }}
            {{ userProfile?.nameLast }}
            <v-tooltip
              v-if="isNameTruncated"
              activator="parent"
              location="bottom"
              content-class="application-picker__tooltip"
            >
              {{ fullName }}
            </v-tooltip>
          </div>
          <!--
            Score badge and origin chip. They ride the first line next to the name until the name
            would get too narrow, and then take a full-width second line of their own — `order-1`
            keeps the name and the actions on the first line. This group never wraps internally, so
            there is never a third line: the badge truncates its job title instead.
          -->
          <div
            class="flex min-w-0 shrink-0 flex-nowrap items-center"
            :class="isWrapped ? 'order-1 mt-1.5 w-full basis-full' : 'ml-3'"
          >
            <div
              ref="scoreBadge"
              class="mr-1 min-w-0 shrink-0 rounded bg-tint-40 font-sans text-sm"
            >
              <ApplicationScoreHoverWrapper
                v-if="jobApplicant?.job?.id && jobApplicant?.id"
                :application-id="jobApplicant?.id"
                :initiated-by-copilot="jobApplicant?.initiatedByCopilot"
                :initiated-by-outreach="jobApplicant?.initiatedByOutreach"
                :job-title="projectName"
                :job-id="jobApplicant?.job?.id"
                :fallback-score="jobApplicant?.score"
                :load-on-mount="true"
                popup-location="bottom center"
                analytics-source="Conversation Top Banner"
                @change:pro-score="emit('change:pro-score', $event)"
              >
                <template #hoverTrigger="{ scoreDetails, scoreIsLoading }">
                  <div class="flex items-center rounded bg-tint-40 p-0.5 transition-all">
                    <template v-if="scoreIsLoading">
                      <div class="flex items-center gap-2">
                        <div
                          class="flex h-[18px] w-[18px] items-center justify-center rounded border-2 border-white bg-tint-200"
                        >
                          <div
                            class="flex h-2.5 w-2.5 animate-spin rounded-full border-2 border-black border-t-tint-200"
                          ></div>
                        </div>

                        <span class="text-xs text-shade-820">Loading...</span>
                      </div>
                    </template>
                    <template v-else>
                      <ApplicationScoreCompactDisplay
                        :score="scoreDetails?.score"
                        :job-title="projectName"
                        :hide-job-title="hideJobTitle"
                        not-scored-bg-class="bg-tint-60"
                      />
                    </template>
                  </div>
                </template>
              </ApplicationScoreHoverWrapper>
            </div>
            <!--
              `!h-6` overrides the chip's own 20px height here only, so it stands 24px. It rides on
              the chip itself rather than either label variant, so the icon-only form the ladder
              collapses to is the same height.
            -->
            <!-- Measure both chips together so DNC participates in header collapsing. -->
            <div ref="originChip" class="flex shrink-0 items-center">
              <ApplicantOriginChip
                :integration-id="atsIntegrationId"
                :is-ai-sourced="showCopilotOutreach"
                :is-sourced="showManualSourced"
                :icon-only="isOriginChipIconOnly"
                class="ml-1 !h-6 shrink-0"
              />
              <DoNotContactChip v-if="isDoNotContact" class="ml-1 shrink-0" />
            </div>
          </div>
          <div ref="headerActions" class="ml-auto flex h-7 shrink-0 items-center gap-2">
            <ApplicationStatusPicker
              v-if="jobApplicant"
              slim
              :status="jobApplicant?.status"
              :rejection-reason="jobApplicant?.rejectionReason"
              @update-value="
                ({ newStatus, previousStatus, reason, isSilent }) =>
                  updateApplicationStatus(newStatus, previousStatus, reason, isSilent)
              "
            />
            <InterviewSchedulingTrigger
              v-if="hasInterviewSchedulingAccess"
              variant="compact"
              :disabled="conversationIndexService.isMessagingDoNotContact"
              :short-label="shortInterviewLabel"
            />
            <InterviewSchedulingGatedButton
              v-else
              variant="compact"
              :short-label="shortInterviewLabel"
            />
            <DownloadProfile />
            <ProHeaderActionsMenu
              :is-do-not-contact="isDoNotContact"
              @mark-do-not-contact="openDoNotContactModal('mark')"
              @remove-do-not-contact="openDoNotContactModal('remove')"
            />
          </div>
        </div>
        <ProHeaderContactDetails />
      </div>
    </div>
    <DoNotContactModal
      v-model="isDoNotContactModalOpen"
      :variant="doNotContactModalVariant"
      :candidate-name="fullName"
      :is-loading="isDoNotContactSubmitting"
      @submit="submitDoNotContact"
    />
  </section>
</template>
